import AdminLayout from "../../layouts/AdminLayout";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import apiClient from "../../api/apiClient";
import {
  updateCalendar,
  bulkUploadCalendar,
} from "../../api/attendanceService";

const STATUS_CHOICES = [
  { value: "HOLIDAY", label: "Holiday" },
  { value: "WEEK_OFF", label: "Week Off" },
  { value: "WORKDAY", label: "Working Day" },
];

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function parseDateSafe(dateStr) {
  if (!dateStr) return new Date(NaN);
  if (dateStr.includes("T")) return new Date(dateStr);
  return new Date(dateStr + "T00:00");
}

function formatDate(dateStr) {
  const d = parseDateSafe(dateStr);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function getLocalMonth(dateStr) {
  return parseDateSafe(dateStr).getMonth() + 1;
}

function getLocalYear(dateStr) {
  return parseDateSafe(dateStr).getFullYear();
}

function ManageCalendar() {
  const [date, setDate] = useState("");
  const [status, setStatus] = useState("");
  const [description, setDescription] = useState("");
  const [selectedLocations, setSelectedLocations] = useState([]); // ✅ Added multiselect state
  const [editId, setEditId] = useState(null);
  const [bulkFile, setBulkFile] = useState(null);

  const [calendarDays, setCalendarDays] = useState([]);
  const [locations, setLocations] = useState([]);

  // Weekend Week-off rule states
  const [weekOffRules, setWeekOffRules] = useState([]);
  const [ruleChoices, setRuleChoices] = useState([]);
  const [selectedRuleLocations, setSelectedRuleLocations] = useState([]);
  const [rulePattern, setRulePattern] = useState("");
  const [applyYear, setApplyYear] = useState(new Date().getFullYear().toString());
  const [applyingRules, setApplyingRules] = useState(false);
  const [savingRule, setSavingRule] = useState(false);

  const currentMonthNum = new Date().getMonth() + 1;
  const currentYearNum = new Date().getFullYear();

  const [filterLocation, setFilterLocation] = useState("");
  const [filterMonth, setFilterMonth] = useState(currentMonthNum.toString());
  const [filterYear, setFilterYear] = useState(currentYearNum.toString());
  const [searchDate, setSearchDate] = useState("");

  const availableYears = Array.from(
    new Set(calendarDays.map((d) => d._year))
  ).sort((a, b) => b - a);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const res = await apiClient.get("/accounts/api/locations/");
        setLocations(res.data.locations || []);
      } catch (err) {
        console.error("Failed to load locations", err);
      }
    };
    fetchLocations();
  }, []);

  const fetchCalendar = async () => {
    try {
      const res = await apiClient.get("/attendance/api/manage-calendar/");
      const formatted = (res.data.calendar_days || []).map((d) => ({
        ...d,
        dateDisplay: formatDate(d.date),
        _month: getLocalMonth(d.date),
        _year: getLocalYear(d.date),
      }));
      setCalendarDays(formatted);
      setWeekOffRules(res.data.week_off_rules || []);
      setRuleChoices(res.data.rule_choices || []);
    } catch (err) {
      console.error("Calendar load error:", err);
    }
  };

  useEffect(() => {
    fetchCalendar();
  }, []);

  const filteredCalendarDays = calendarDays.filter((d) => {
    const dateMatch = !searchDate || d.date === searchDate;
    const locMatch =
      !filterLocation ||
      (filterLocation === "__GLOBAL__"
        ? !d.location
        : d.location === filterLocation ||
          (d.location && d.location.split(",").includes(filterLocation)));
    const monthMatch = !filterMonth || d._month === parseInt(filterMonth);
    const yearMatch = !filterYear || d._year === parseInt(filterYear);
    return dateMatch && locMatch && monthMatch && yearMatch;
  });

  // ── FIX: send location_code (not location) so backend reads it correctly ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    // Check if entry with same date already exists
    const duplicate = calendarDays.find((d) => d.date === date && d.id !== editId);
    if (duplicate) {
      alert("❌ A calendar entry already exists for this date. Please edit/update the existing entry instead.");
      return;
    }
    try {
      if (editId) {
        await updateCalendar({
          action: "update",
          calendar_day_id: editId,
          date,
          status,
          description,
          location_codes: selectedLocations,
        });
      } else {
        await updateCalendar({
          action: "add",
          date,
          status,
          description,
          location_codes: selectedLocations,
        });
      }
      resetForm();
      fetchCalendar();
      alert("✅ Calendar saved");
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || "Operation failed");
    }
  };

  const resetForm = () => {
    setDate("");
    setStatus("");
    setDescription("");
    setSelectedLocations([]);
    setEditId(null);
  };

  const handleEdit = (day) => {
    setEditId(day.id);
    setDate(day.date);
    setStatus(day.status);
    setDescription(day.description || "");
    
    // Split the comma-separated location codes if present
    const locs = day.location
      ? day.location.split(",").map(c => c.trim())
      : [];
    setSelectedLocations(locs);
    
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this calendar entry?")) return;
    try {
      await updateCalendar({ action: "delete", calendar_day_id: id });
      fetchCalendar();
      alert("🗑️ Entry deleted");
    } catch (err) {
      console.error(err);
    }
  };

  const handleBulkUpload = async (e) => {
    e.preventDefault();
    if (!bulkFile) { alert("Please select CSV file"); return; }
    try {
      const formData = new FormData();
      formData.append("action", "bulk_upload");
      formData.append("bulk_file", bulkFile);
      await bulkUploadCalendar(formData);
      alert("CSV uploaded successfully");
      setBulkFile(null);
      fetchCalendar();
    } catch (err) {
      console.error(err.response?.data);
      alert(err.response?.data?.error || "Upload failed");
    }
  };

  const clearFilters = () => {
    setSearchDate("");
    setFilterLocation("");
    setFilterMonth("");
    setFilterYear("");
  };

  const handleSaveRule = async (e) => {
    e.preventDefault();
    if (selectedRuleLocations.length === 0 || !rulePattern) {
      alert("Please select at least one Location and a Rule Pattern.");
      return;
    }
    setSavingRule(true);
    try {
      await apiClient.post("/attendance/api/manage-calendar/", {
        action: "add_rule",
        location_codes: selectedRuleLocations,
        rule_pattern: rulePattern,
      });
      alert("✅ Location week-off rule saved!");
      setSelectedRuleLocations([]);
      setRulePattern("");
      fetchCalendar();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || "Failed to save rule");
    } finally {
      setSavingRule(false);
    }
  };

  const handleDeleteRule = async (id) => {
    if (!window.confirm("Delete this week-off rule?")) return;
    try {
      await apiClient.post("/attendance/api/manage-calendar/", {
        action: "delete_rule",
        rule_id: id,
      });
      alert("🗑️ Rule deleted");
      fetchCalendar();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || "Failed to delete rule");
    }
  };

  const handleApplyRules = async (e) => {
    e.preventDefault();
    if (!applyYear) {
      alert("Please enter a valid year.");
      return;
    }
    // If there is any location with rule pattern NONE, show a warning popup first
    const hasNonePattern = weekOffRules.some((r) => r.rule_pattern === "NONE");
    if (hasNonePattern) {
      const confirmed = window.confirm(
        `⚠️ Warning: Locations configured with the 'None' rule pattern will have all their auto-generated weekend week-offs for the year ${applyYear} deleted from the calendar, and associated attendance records will be reverted. Are you sure you want to proceed?`
      );
      if (!confirmed) {
        return;
      }
    }
    setApplyingRules(true);
    try {
      const res = await apiClient.post("/attendance/api/manage-calendar/", {
        action: "apply_rules",
        year: applyYear,
      });
      alert(`✅ ${res.data.message || "Weekend rules applied successfully!"}`);
      fetchCalendar();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || "Failed to apply weekend rules");
    } finally {
      setApplyingRules(false);
    }
  };

  const hasActiveFilters = searchDate || filterLocation || filterMonth || filterYear;

  const statusColor = (s) => {
    if (s === "HOLIDAY") return "bg-red-500/30 text-red-200 border-red-400/30";
    if (s === "WEEK_OFF") return "bg-yellow-500/30 text-yellow-200 border-yellow-400/30";
    return "bg-emerald-500/30 text-emerald-200 border-emerald-400/30";
  };

  return (
    <AdminLayout>
      <div className="p-3 sm:p-6">
        <div className="w-full max-w-[1400px] mx-auto">

          {/* HEADER */}
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8"
          >
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shadow-lg flex-shrink-0">
              <span className="text-2xl sm:text-3xl">📅</span>
            </div>
            <div>
              <h1 className="text-xl sm:text-3xl font-bold text-white">Editorial Calendar</h1>
              <p className="text-white/60 text-xs sm:text-sm">Manage holidays, week-offs per location</p>
            </div>
          </motion.div>

          {/* FORM + BULK UPLOAD CARDS */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">

            {/* ADD / UPDATE FORM */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl p-4 sm:p-8"
            >
              <h3 className="text-lg sm:text-xl font-bold text-white mb-4 sm:mb-6">
                {editId ? "✏️ Update Calendar Entry" : "➕ Add New Calendar Entry"}
              </h3>

              <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                <div>
                  <label className="text-white/70 text-xs mb-1 block">Date</label>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    required
                    className="w-full bg-white/10 border border-white/30 rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 text-white text-sm focus:outline-none focus:border-blue-400"
                  />
                </div>

                <div>
                  <label className="text-white/70 text-xs mb-1 block">Status</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    required
                    className="w-full bg-white/10 border border-white/30 rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 text-white text-sm focus:outline-none focus:border-blue-400"
                  >
                    <option value="" className="bg-gray-800">Select Status</option>
                    {STATUS_CHOICES.map((s) => (
                      <option key={s.value} value={s.value} className="bg-gray-800">
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-white/70 text-xs mb-2 block font-semibold">
                    📍 Select Locations
                    <span className="text-white/45 block text-[10px] mt-0.5 leading-none">
                      Select "Global" or choose one or more individual locations.
                    </span>
                  </label>
                  
                  <div className="flex flex-wrap gap-2 p-3 bg-white/5 border border-white/10 rounded-2xl select-none">
                    {/* Global Pill */}
                    <button
                      type="button"
                      onClick={() => setSelectedLocations([])}
                      className={`px-4 py-2 rounded-xl text-xs font-semibold uppercase tracking-wider transition-all border ${
                        selectedLocations.length === 0
                          ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white border-blue-400 shadow-md shadow-blue-500/25"
                          : "bg-white/5 border-white/10 text-white/50 hover:text-white hover:bg-white/10"
                      }`}
                    >
                      🌐 Global (All)
                    </button>

                    {/* Individual Location Pills */}
                    {locations.map((l) => {
                      const isSelected = selectedLocations.includes(l.code);
                      return (
                        <button
                          key={l.code}
                          type="button"
                          onClick={() => {
                            setSelectedLocations((prev) => {
                              if (prev.includes(l.code)) {
                                return prev.filter((c) => c !== l.code);
                              } else {
                                return [...prev, l.code];
                              }
                            });
                          }}
                          className={`px-4 py-2 rounded-xl text-xs font-semibold uppercase tracking-wider transition-all border ${
                            isSelected
                              ? "bg-gradient-to-r from-fuchsia-600 to-purple-600 text-white border-fuchsia-500 shadow-md shadow-fuchsia-600/25"
                              : "bg-white/5 border-white/10 text-white/50 hover:text-white hover:bg-white/10"
                          }`}
                        >
                          📍 {l.code}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="text-white/70 text-xs mb-1 block">Notes (optional)</label>
                  <textarea
                    rows="3"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Optional notes..."
                    className="w-full bg-white/10 border border-white/30 rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 text-white text-sm resize-none focus:outline-none focus:border-blue-400"
                  />
                </div>

                <div className="flex flex-wrap gap-2 sm:gap-3 justify-end pt-2">
                  {editId && (
                    <>
                      <button
                        type="button"
                        onClick={resetForm}
                        className="px-4 sm:px-5 py-2.5 sm:py-3 bg-white/10 hover:bg-white/20 rounded-xl text-white text-sm font-semibold transition"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(editId)}
                        className="px-4 sm:px-5 py-2.5 sm:py-3 bg-red-600 hover:bg-red-700 rounded-xl text-white text-sm font-bold transition"
                      >
                        Delete
                      </button>
                    </>
                  )}
                  <button
                    type="submit"
                    className="px-5 sm:px-6 py-2.5 sm:py-3 bg-blue-500 hover:bg-blue-600 rounded-xl text-white text-sm font-bold transition"
                  >
                    {editId ? "Update Entry" : "Add Entry"}
                  </button>
                </div>
              </form>
            </motion.div>

            {/* BULK UPLOAD */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl p-4 sm:p-8 flex flex-col items-center justify-center"
            >
              <h3 className="text-lg sm:text-xl font-bold text-white mb-2 text-center">
                📤 Bulk Upload Calendar Days
              </h3>
              <p className="text-white/50 text-xs mb-4 sm:mb-6 text-center">
                CSV columns:{" "}
                <span className="text-white/70 font-semibold">
                  date (DD-MM-YYYY), status, description, location_code
                </span>
                <br />
                Leave location_code blank for global entries.
              </p>

              <form onSubmit={handleBulkUpload} className="w-full flex flex-col items-center space-y-4 sm:space-y-6">
                <label className="w-full cursor-pointer">
                  <div className="w-full bg-white/10 border-2 border-dashed border-white/30 rounded-xl px-4 sm:px-6 py-6 sm:py-8 text-center hover:border-green-400 transition-all">
                    <p className="text-white text-sm font-semibold">
                      {bulkFile ? bulkFile.name : "Click to Choose CSV File"}
                    </p>
                    <p className="text-white/50 text-xs mt-2">Supported format: .csv</p>
                  </div>
                  <input
                    type="file"
                    accept=".csv"
                    required
                    onChange={(e) => setBulkFile(e.target.files[0])}
                    className="hidden"
                  />
                </label>
                <button
                  type="submit"
                  className="w-full sm:w-auto px-6 sm:px-8 py-2.5 sm:py-3 bg-green-500 hover:bg-green-600 rounded-xl text-white font-bold shadow-lg transition-all text-sm"
                >
                  Upload CSV File
                </button>
              </form>
            </motion.div>
          </div>

          {/* LOCATION WEEK-OFF RULES & AUTO GENERATION */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">

            {/* RULE DEFINITION PANEL */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.12 }}
              className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl p-4 sm:p-8 lg:col-span-2"
            >
              <h3 className="text-lg sm:text-xl font-bold text-white mb-4 sm:mb-6">
                📍 Location Week-off Rules
              </h3>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
                {/* Left side: Configure Weekend Pattern */}
                <div>
                  <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-1.5">
                    ⚙️ Configure Weekend Pattern
                  </h4>
                  <form onSubmit={handleSaveRule} className="space-y-3 sm:space-y-4">
                    <div className="space-y-3">
                      <div>
                        <label className="text-white/70 text-xs mb-2 block font-semibold">
                          📍 Select Locations
                          <span className="text-white/45 block text-[10px] mt-0.5 leading-none">
                            Choose one or more locations to apply this rule to.
                          </span>
                        </label>
                        
                        <div className="flex flex-wrap gap-2 p-3 bg-white/5 border border-white/10 rounded-2xl select-none">
                          {/* Global Pill */}
                          <button
                            type="button"
                            onClick={() => setSelectedRuleLocations(["__GLOBAL__"])}
                            className={`px-4 py-2 rounded-xl text-xs font-semibold uppercase tracking-wider transition-all border ${
                              selectedRuleLocations.includes("__GLOBAL__")
                                ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white border-blue-400 shadow-md shadow-blue-500/25"
                                : "bg-white/5 border-white/10 text-white/50 hover:text-white hover:bg-white/10"
                            }`}
                          >
                            🌐 Global (All)
                          </button>

                          {/* Individual Location Pills */}
                          {locations.map((l) => {
                            const isSelected = selectedRuleLocations.includes(l.code);
                            return (
                              <button
                                key={l.code}
                                type="button"
                                onClick={() => {
                                  setSelectedRuleLocations((prev) => {
                                    const filtered = prev.filter(c => c !== "__GLOBAL__");
                                    if (filtered.includes(l.code)) {
                                      return filtered.filter((c) => c !== l.code);
                                    } else {
                                      return [...filtered, l.code];
                                    }
                                  });
                                }}
                                className={`px-4 py-2 rounded-xl text-xs font-semibold uppercase tracking-wider transition-all border ${
                                  isSelected
                                    ? "bg-gradient-to-r from-fuchsia-600 to-purple-600 text-white border-fuchsia-500 shadow-md shadow-fuchsia-600/25"
                                    : "bg-white/5 border-white/10 text-white/50 hover:text-white hover:bg-white/10"
                                }`}
                              >
                                📍 {l.code}
                              </button>
                            );
                          })}
                          {locations.length === 0 && (
                            <p className="text-white/40 text-xs italic">No locations available</p>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="text-white/70 text-xs mb-1 block">Rule Pattern</label>
                        <select
                          value={rulePattern}
                          onChange={(e) => setRulePattern(e.target.value)}
                          required
                          className="w-full bg-white/10 border border-white/30 rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 text-white text-sm focus:outline-none focus:border-blue-400"
                        >
                          <option value="" className="bg-gray-800">Select Pattern</option>
                          {ruleChoices.map((c) => (
                            <option key={c.value} value={c.value} className="bg-gray-800">
                              {c.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={savingRule}
                        className="px-5 py-2.5 bg-blue-500 hover:bg-blue-600 disabled:opacity-55 rounded-xl text-white text-sm font-bold transition shadow-lg w-full sm:w-auto"
                      >
                        {savingRule ? "Saving..." : "Save Rule"}
                      </button>
                    </div>
                  </form>
                </div>

                {/* Right side: Configured Rules and Apply Target Year */}
                <div className="flex flex-col justify-between space-y-6 border-t lg:border-t-0 lg:border-l border-white/10 pt-6 lg:pt-0 lg:pl-8">
                  {/* RULES LIST */}
                  <div>
                    <h4 className="text-sm font-bold text-white mb-3">Configured Location Rules</h4>
                    {weekOffRules.length === 0 ? (
                      <p className="text-white/40 text-xs italic">No location rules defined yet.</p>
                    ) : (
                      <div className="overflow-x-auto max-h-[160px] overflow-y-auto no-scrollbar">
                        <table className="w-full text-xs text-left text-white/80">
                          <thead>
                            <tr className="border-b border-white/10 text-white/50 uppercase font-semibold">
                              <th className="py-2 px-1">Location</th>
                              <th className="py-2 px-1">Pattern</th>
                              <th className="py-2 px-1 text-right">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {weekOffRules.map((rule) => (
                              <tr key={rule.id} className="border-b border-white/5 hover:bg-white/5">
                                <td className="py-2 px-1 font-semibold">{rule.location_code}</td>
                                <td className="py-2 px-1">{rule.rule_pattern_display}</td>
                                <td className="py-2 px-1 text-right">
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteRule(rule.id)}
                                    className="px-2 py-1 bg-red-500/20 hover:bg-red-500/40 border border-red-500/30 text-red-300 rounded-lg transition"
                                  >
                                    Delete
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* AUTO GENERATION */}
                  <div className="border-t border-white/10 pt-4 mt-auto">
                    <h4 className="text-sm font-bold text-white mb-1.5">
                      ⚡ Apply Rules & Generate Week-offs
                    </h4>
                    <p className="text-white/50 text-[11px] mb-3 leading-relaxed">
                      Automatically generate location-specific weekend week-off entries on the calendar for a whole year.
                      <span className="text-amber-300/80 font-medium"> Note:</span> This will not overwrite manually defined holidays or existing workday calendar overrides.
                    </p>

                    <form onSubmit={handleApplyRules} className="flex flex-col sm:flex-row items-end gap-3">
                      <div className="w-full sm:w-1/3">
                        <label className="text-white/70 text-[11px] mb-1 block font-semibold">Target Year</label>
                        <input
                          type="number"
                          min="2020"
                          max="2035"
                          value={applyYear}
                          onChange={(e) => setApplyYear(e.target.value)}
                          required
                          className="w-full bg-white/10 border border-white/30 rounded-xl px-3 sm:px-4 py-2 text-white text-sm focus:outline-none focus:border-blue-400"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={applyingRules}
                        className="w-full sm:w-2/3 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 disabled:opacity-55 text-white font-bold rounded-xl shadow-lg transition-all text-xs sm:text-sm uppercase tracking-wider"
                      >
                        {applyingRules ? "Generating Week-Offs..." : "Apply & Generate Week-offs"}
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* SEARCH & FILTER BAR */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.14 }}
            className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl p-4 sm:p-6 mb-6"
          >
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
              <div className="flex-1">
                <h4 className="text-sm font-bold text-white mb-2 flex items-center gap-1.5">
                  🔍 Search & Filter Calendar Entries
                </h4>
                <p className="text-white/40 text-xs mb-4">
                  Find entries by selecting a specific date or filtering by location, month, and year.
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                  {/* DATE SEARCH */}
                  <div className="flex flex-col gap-1">
                    <label className="text-white/70 text-xs font-semibold pl-1">
                      📅 Search Date
                    </label>
                    <input
                      type="date"
                      value={searchDate}
                      onChange={(e) => setSearchDate(e.target.value)}
                      className="bg-slate-800 border border-white/20 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-400 cursor-pointer w-full"
                    />
                  </div>

                  {/* LOCATION FILTER */}
                  <div className="flex flex-col gap-1">
                    <label className="text-white/70 text-xs font-semibold pl-1">
                      📍 Location
                    </label>
                    <select
                      value={filterLocation}
                      onChange={(e) => setFilterLocation(e.target.value)}
                      className="bg-slate-800 border border-white/20 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-400 cursor-pointer w-full"
                    >
                      <option value="" className="bg-slate-800">All Locations</option>
                      {locations.map((l) => (
                        <option key={l.code} value={l.code} className="bg-slate-800">
                          {l.code} — {l.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* MONTH FILTER */}
                  <div className="flex flex-col gap-1">
                    <label className="text-white/70 text-xs font-semibold pl-1">
                      🗓 Month
                    </label>
                    <select
                      value={filterMonth}
                      onChange={(e) => setFilterMonth(e.target.value)}
                      className="bg-slate-800 border border-white/20 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-400 cursor-pointer w-full"
                    >
                      <option value="" className="bg-slate-800">All Months</option>
                      {MONTH_NAMES.map((name, i) => (
                        <option key={i + 1} value={i + 1} className="bg-slate-800">
                          {name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* YEAR FILTER */}
                  <div className="flex flex-col gap-1">
                    <label className="text-white/70 text-xs font-semibold pl-1">
                      📆 Year
                    </label>
                    <select
                      value={filterYear}
                      onChange={(e) => setFilterYear(e.target.value)}
                      className="bg-slate-800 border border-white/20 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-400 cursor-pointer w-full"
                    >
                      <option value="" className="bg-slate-800">All Years</option>
                      {availableYears.map((yr) => (
                        <option key={yr} value={yr} className="bg-slate-800">
                          {yr}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* CLEAR FILTERS */}
              {hasActiveFilters && (
                <div className="flex-shrink-0 self-end md:mb-1">
                  <button
                    onClick={clearFilters}
                    className="w-full md:w-auto px-4 py-2 bg-red-500/20 hover:bg-red-500/40 border border-red-400/30 text-red-300 text-sm font-semibold rounded-xl transition-all"
                  >
                    ✕ Clear Filters
                  </button>
                </div>
              )}
            </div>
          </motion.div>

          {/* EXISTING CALENDAR ENTRIES TABLE */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl mb-6"
          >
            {/* TABLE HEADER with filters */}
            <div className="px-4 sm:px-6 py-4 bg-white/10 border-b border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-white">Existing Calendar Entries</h3>
                  <p className="text-white/40 text-xs mt-0.5">
                    Showing{" "}
                    <span className="text-white/70 font-semibold">{filteredCalendarDays.length}</span>
                    {" "}of{" "}
                    <span className="text-white/70 font-semibold">{calendarDays.length}</span>
                    {" "}entries
                  </p>
                </div>
              </div>
            </div>

            {/* TABLE */}
            <div className="overflow-x-auto">
              {filteredCalendarDays.length === 0 ? (
                <div className="py-12 sm:py-16 text-center text-white/40 text-sm">
                  No calendar entries found for the selected filters.
                </div>
              ) : (
                <>
                  {/* Desktop Table */}
                  <table className="w-full text-sm text-center hidden sm:table">
                    <thead>
                      <tr className="bg-white/10">
                        <th className="py-4 text-white/70 font-semibold px-4">Date</th>
                        <th className="text-white/70 font-semibold px-4">Status</th>
                        <th className="text-white/70 font-semibold px-4">Location</th>
                        <th className="text-white/70 font-semibold px-4">Notes</th>
                        <th className="text-white/70 font-semibold px-4">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCalendarDays.map((day) => (
                        <tr
                          key={day.id}
                          className="border-b border-white/10 hover:bg-white/5 transition"
                        >
                          <td className="py-4 text-white/80 px-4 font-medium">
                            {day.dateDisplay}
                          </td>
                          <td className="px-4">
                            <span className={`px-3 py-1 rounded-lg text-xs border font-semibold ${statusColor(day.status)}`}>
                              {day.status}
                            </span>
                          </td>
                          <td className="px-4">
                            <span className={`px-3 py-1 rounded-lg text-xs border font-semibold ${day.location
                              ? "bg-blue-500/20 text-blue-200 border-blue-400/30"
                              : "bg-white/10 text-white/50 border-white/20"
                              }`}>
                              {day.location_name || "All Locations"}
                            </span>
                          </td>
                          <td className="text-white/60 px-4 max-w-[200px] truncate">
                            {day.description || "—"}
                          </td>
                          <td className="px-4">
                            <button
                              onClick={() => handleEdit(day)}
                              className="mx-1 px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white text-xs font-semibold transition"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(day.id)}
                              className="mx-1 px-4 py-2 bg-red-500 hover:bg-red-600 rounded-lg text-white text-xs font-semibold transition"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Mobile Cards */}
                  <div className="sm:hidden divide-y divide-white/10">
                    {filteredCalendarDays.map((day) => (
                      <div key={day.id} className="p-4 hover:bg-white/5 transition">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div>
                            <p className="text-white font-semibold text-sm">{day.dateDisplay}</p>
                            <p className="text-white/50 text-xs mt-0.5">{day.description || "No notes"}</p>
                          </div>
                          <span className={`px-2.5 py-1 rounded-lg text-xs border font-semibold flex-shrink-0 ${statusColor(day.status)}`}>
                            {day.status}
                          </span>
                        </div>
                        <div className="flex items-center justify-between mt-3">
                          <span className={`px-2.5 py-1 rounded-lg text-xs border font-semibold ${day.location
                            ? "bg-blue-500/20 text-blue-200 border-blue-400/30"
                            : "bg-white/10 text-white/50 border-white/20"
                            }`}>
                            {day.location_name || "All Locations"}
                          </span>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEdit(day)}
                              className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 rounded-lg text-white text-xs font-semibold transition"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(day.id)}
                              className="px-3 py-1.5 bg-red-500 hover:bg-red-600 rounded-lg text-white text-xs font-semibold transition"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </motion.div>

        </div>
      </div>
    </AdminLayout>
  );
}

export default ManageCalendar;